import { useMemo, useState } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import {
  ActionableNotification,
  Button,
  Checkbox,
  ComposedModal,
  InlineLoading,
  ModalBody,
  ModalFooter,
  ModalHeader,
  TextInput,
} from '@carbon/react';
import { Add, Edit, TrashCan } from '@carbon/react/icons';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from 'components/accordion/accordion';
import { chartsAtom, chartsShowAllStateAtom } from '../atoms';
import { useEval3Page } from '../context';
import { useChartCatalog } from '../hooks';
import type { Chart, ChartSpec, ChartToggle } from './types';
import styles from './charts-visuals-section.module.scss';

/**
 * The Edit (rename) chart modal (mock §4d) — a name-only editor today: the chart-type /
 * axis configuration lands when this grows to mirror `attribute-filter-modal`. Owns its
 * draft name (seeded from `initialName`) so a reopen re-seeds cleanly, and gates Save
 * on a non-empty trimmed name. Mirrors the eval3-new modal chrome (`ComposedModal open`
 * + `ModalHeader` / `ModalBody` / `ModalFooter`).
 *
 * @param {object} props - The seed `initialName`, the `title` / `submitLabel`, and the `onSubmit` / `onClose` handlers.
 * @return {JSX.Element} The chart name modal.
 */
function ChartModal({
  initialName,
  title,
  submitLabel,
  onSubmit,
  onClose,
}: {
  initialName: string;
  title: string;
  submitLabel: string;
  onSubmit: (name: string) => void;
  onClose: () => void;
}) {
  const [name, setName] = useState(initialName);
  const trimmed = name.trim();
  return (
    <ComposedModal open onClose={onClose} size="sm">
      <ModalHeader title={title} />
      <ModalBody>
        <TextInput
          id="eval3-chart-name"
          labelText="Chart name"
          placeholder="e.g. Breakeven Analysis"
          value={name}
          onChange={(evt) => setName(evt.target.value)}
        />
      </ModalBody>
      <ModalFooter>
        <Button kind="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button
          kind="primary"
          disabled={trimmed.length === 0}
          onClick={() => onSubmit(trimmed)}
        >
          {submitLabel}
        </Button>
      </ModalFooter>
    </ComposedModal>
  );
}

/**
 * The Add Chart picker (mock §4d): a checklist of the backend `chart_catalog` presets
 * (passed in as `catalog`, each entry carrying an `added` flag = already on the page).
 * While the catalog is loading it shows a spinner, and a fetch error shows an actionable
 * notification with a Retry that re-runs the catalog fetch (`onRetry` → the hook's
 * `refetch`) — matching the eval3 drill-in / chart-status pattern. There is no hardcoded
 * list, so the checklist only renders once the backend responds.
 *
 * Mirrors the eval3 filter modals (`attribute-filter-modal`): a chart already on the page
 * shows CHECKED (not disabled) — unchecking it removes it, checking a new one adds it — so
 * the picker is a live mirror of page membership. It tracks only the user's deviations from
 * the `added` baseline, so a fresh open (a new mount) always reflects the latest page state
 * (charts added / renamed / deleted since). Apply is gated until ≥1 checkbox changes and
 * commits the whole diff at once. A preset the backend reports as unavailable — and not
 * already on the page — stays disabled with its reason. Mirrors the eval3-new modal chrome.
 *
 * @param {object} props - The `catalog` (each entry carrying its `added` flag), its `isLoading` / `error` fetch state, and the `onApply` / `onRetry` / `onClose` handlers.
 * @return {JSX.Element} The Add Chart picker modal.
 */
function AddChartModal({
  catalog,
  isLoading,
  error,
  onAdd,
  onRetry,
  onClose,
}: {
  catalog: {
    id: string;
    name: string;
    available: boolean;
    reason?: string;
    added: boolean;
  }[];
  isLoading: boolean;
  error: string | null;
  onAdd: (additions: string[], removals: string[]) => void;
  onRetry: () => void;
  onClose: () => void;
}) {
  // The user's deviations from the current page membership: catalog `id`s they've flipped
  // away from the `added` baseline. Starts empty (a fresh open re-seeds from `added`), so a
  // row's checked state is `added` XOR "flipped here".
  const [toggled, setToggled] = useState<Set<string>>(new Set());

  const flip = (id: string) => {
    setToggled((current) => {
      const next = new Set(current);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // Split the deviations into additions (now checked, wasn't on the page) and removals
  // (now unchecked, was on the page) — every flipped id is exactly one or the other.
  const handleApply = () => {
    const additions: string[] = [];
    const removals: string[] = [];
    catalog.forEach(({ id, added }) => {
      if (!toggled.has(id)) {
        return;
      }
      (added ? removals : additions).push(id);
    });
    onAdd(additions, removals);
  };

  return (
    <ComposedModal open onClose={onClose}>
      <ModalHeader title="Add Chart" />
      <ModalBody>
        <p className={styles.addChartHint}>
          Select the charts to show on the page.
        </p>
        {isLoading && catalog.length === 0 ? (
          <InlineLoading description="Loading charts…" />
        ) : error && catalog.length === 0 ? (
          <ActionableNotification
            inline
            kind="error"
            lowContrast
            hideCloseButton
            title="Couldn't load charts"
            subtitle={error}
            actionButtonLabel="Retry"
            onActionButtonClick={onRetry}
          />
        ) : catalog.length === 0 ? (
          <p className={styles.addChartHint}>No charts available.</p>
        ) : (
          // No visible <legend> (the modal title labels the group), so name the
          // checkbox group for assistive tech via aria-label.
          <fieldset
            className={styles.addChartList}
            aria-label="Available charts"
          >
            {catalog.map(({ id, name, available, reason, added }) => {
              // A row's checked state mirrors page membership: `added`, flipped by any local
              // toggle. Disable only an unavailable preset (economics presets; every preset
              // in package mode) that isn't already on the page — an added chart stays
              // interactive so it can be unchecked to remove it. The reason rides in the
              // label so it's visible without a hover.
              const checked = toggled.has(id) ? !added : added;
              const disabled = !available && !added;
              const labelText = disabled
                ? `${name} — ${reason ?? 'Not available'}`
                : name;
              return (
                <Checkbox
                  key={id}
                  id={`eval3-add-chart-${id}`}
                  labelText={labelText}
                  checked={checked}
                  disabled={disabled}
                  onChange={() => flip(id)}
                />
              );
            })}
          </fieldset>
        )}
      </ModalBody>
      <ModalFooter>
        <Button kind="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button
          kind="primary"
          disabled={toggled.size === 0}
          onClick={handleApply}
        >
          Add
        </Button>
      </ModalFooter>
    </ComposedModal>
  );
}

/**
 * The Charts & Visuals accordion item (mock §4d), one section in the Page Controls
 * panel's accordion stack — it owns its own accordion chrome (mirroring
 * `DatasetFilterAccordion`), so the panel just renders `<ChartsVisualsSection />`.
 * Owns its own charts state end-to-end (the
 * component-owned-state convention): the `chartsAtom` list + its derived tri-state
 * "Show All", the Add Chart picker's open flag, and the chart the Edit rename modal is
 * editing. Add opens the catalog picker — a live mirror of page membership that adds the
 * newly-checked charts and removes the unchecked ones on Apply; each row's checkbox drives
 * `visible`, Edit reopens the rename modal, Delete removes.
 * Per-eval + persisted: the headless `ChartsPersistenceSync` hydrates this list (and the
 * work-area grid) from `CalcDetail.chartConfig` on load and autosaves changes back.
 *
 * @return {JSX.Element} The Charts & Visuals accordion item.
 */
export function ChartsVisualsSection() {
  const [charts, dispatchCharts] = useAtom(chartsAtom);
  const chartsShowAllState = useAtomValue(chartsShowAllStateAtom);
  // The Add Chart picker's open flag, and (separately) the chart the Edit rename modal
  // is editing (`null` = closed).
  const [addChartOpen, setAddChartOpen] = useState(false);
  const [editChart, setEditChart] = useState<Chart | null>(null);

  // The Add Chart list is served ENTIRELY by the backend catalog (`POST …/chart_catalog`) —
  // no hardcoded list. Each entry keeps the backend `id` as its identity (no FE type map),
  // carrying the backend `label` + `availability` + `spec`. Entries the
  // backend reports as `available:false` (the economics presets) render disabled with their
  // reason. The fetch is gated on the picker being open (`enabled: addChartOpen`) AND on a
  // calc existing (the contract scopes by `calculationId` alone), so it fires when Add Chart
  // is clicked in calc mode — NOT on page load — then stays cached (staleTime) for later
  // opens. In package mode (no calc) it does not fire and the list is empty. The modal shows
  // a spinner / error while the first fetch is in flight.
  const { calculationId } = useEval3Page();
  const {
    data: catalogData,
    isFetching: catalogLoading,
    error: catalogError,
    refetch: refetchCatalog,
  } = useChartCatalog({ calculationId }, { enabled: addChartOpen });
  const catalog = useMemo<
    {
      id: string;
      name: string;
      available: boolean;
      reason?: string;
      added: boolean;
      spec: ChartSpec;
      toggle?: ChartToggle;
    }[]
  >(() => {
    // No calc → no chart catalog. The fetch is gated off without a calc, but React Query's
    // `keepPreviousData` would otherwise keep serving the last-viewed eval's catalog here —
    // so ignore any retained data and show the empty "No charts available" state.
    if (calculationId === null) {
      return [];
    }
    // Merge the two data points the picker needs onto a single catalog entry: the backend
    // catalog (label / availability / spec, keyed by `id` — no FE type map) plus the atom's
    // page state as an `added` flag (this preset is already on the page). The picker seeds
    // each row's checked state from `added` (a chart on the page shows checked), so it never
    // needs a separate set.
    // Every preset is a singleton here: a catalog preset is keyed by its `catalogId`; the
    // `custom` preset is keyed by `source === 'custom'` (it carries no catalogId). So the
    // picker shows "Custom Chart" CHECKED once one is on the page — unchecking it removes it,
    // checking it (when absent) adds one. (One custom instance for now; the picker will grow
    // to a per-instance list + an explicit "add another" action later — out of current scope.)
    const addedCatalogIds = new Set(
      charts
        .filter((chart) => chart.source === 'catalog')
        .map((chart) => chart.catalogId)
    );
    const hasCustomChart = charts.some((chart) => chart.source === 'custom');
    return (catalogData?.charts ?? []).map((entry) => ({
      // Carry the `spec` + `toggle` so the added chart instantiates a generic SpecChart from
      // config (or seeds the Custom builder).
      id: entry.id,
      name: entry.label,
      available: entry.availability.available,
      reason: entry.availability.reason,
      added:
        entry.id === 'custom' ? hasCustomChart : addedCatalogIds.has(entry.id),
      spec: entry.spec,
      toggle: entry.toggle,
    }));
  }, [catalogData, calculationId, charts]);

  /**
   * Sets every chart's visibility to `checked` — the "Show All" toggle action (one
   * atomic `set`, mirroring the Layout Settings "Select all").
   *
   * @param {boolean} checked - The next value applied to all charts.
   * @return {void}
   */
  const toggleAll = (checked: boolean) => {
    dispatchCharts({
      type: 'set',
      charts: charts.map((chart) => ({ ...chart, visible: checked })),
    });
  };

  /**
   * Commits the Add Chart picker's reconciled selection as a single atomic `set`: drops the
   * charts the user unchecked — catalog presets matched by `catalogId`, the `custom` singleton
   * matched by `source === 'custom'` — and appends one new visible chart per newly-checked `id`
   * (`additions`), each with a fresh `instanceId` and its `source` (`custom` → a builder instance
   * with no `catalogId`; else a `catalog` instance keyed by `catalogId`) plus the `spec` /
   * `toggle` the `ChartCard` renders from. Charts left checked are untouched, so their name +
   * visibility survive. An addition id with no catalog match is skipped.
   *
   * @param {string[]} additions - The catalog ids newly checked (to add).
   * @param {string[]} removals - The catalog ids unchecked (to remove from the page).
   * @return {void}
   */
  const handleApplyCharts = (additions: string[], removals: string[]) => {
    const removed = new Set(removals);
    const added = additions.flatMap<Chart>((id) => {
      const entry = catalog.find((candidate) => candidate.id === id);
      if (!entry) {
        return [];
      }
      // The `custom` catalog entry seeds a Custom-builder instance (no catalogId); every other
      // entry is a catalog instance keyed by its catalogId. Each gets a fresh instanceId (its
      // identity + React key). Carry the catalog spec + toggle so the card renders a generic
      // SpecChart / seeds the builder.
      const isCustom = entry.id === 'custom';
      return [
        {
          instanceId: crypto.randomUUID(),
          source: isCustom ? 'custom' : 'catalog',
          ...(isCustom ? {} : { catalogId: entry.id }),
          name: entry.name,
          visible: true,
          spec: entry.spec,
          toggle: entry.toggle,
        },
      ];
    });
    dispatchCharts({
      type: 'set',
      charts: [
        ...charts.filter((chart) => {
          // Custom removal: unchecking the "Custom Chart" singleton drops the custom
          // instance. Catalog removal: drop the preset matched by its `catalogId`.
          if (chart.source === 'custom') {
            return !removed.has('custom');
          }
          return !(
            chart.catalogId !== undefined && removed.has(chart.catalogId)
          );
        }),
        ...added,
      ],
    });
    setAddChartOpen(false);
  };

  /**
   * Commits the Edit rename modal: `update`s the chart in place (matched by `instanceId`,
   * preserving its identity + visibility).
   *
   * @param {string} name - The trimmed chart name from the modal.
   * @return {void}
   */
  const handleRenameSubmit = (name: string) => {
    if (editChart) {
      dispatchCharts({ type: 'update', chart: { ...editChart, name } });
    }
    setEditChart(null);
  };

  const title = (
    <span className={styles.accordionTitle}>Charts &amp; Visuals</span>
  );

  return (
    <Accordion className={styles.accordion}>
      <AccordionSummary>{title}</AccordionSummary>
      <AccordionDetails>
        <div className={styles.chartsSection}>
          <Button
            className={styles.addButton}
            kind="tertiary"
            size="sm"
            renderIcon={Add}
            onClick={() => setAddChartOpen(true)}
          >
            Add Chart
          </Button>

          {charts.length === 0 ? (
            <p className={styles.emptyState}>No charts added.</p>
          ) : (
            <>
              <Checkbox
                id="eval3-charts-show-all"
                labelText="Show All"
                checked={chartsShowAllState === 'checked'}
                indeterminate={chartsShowAllState === 'indeterminate'}
                onChange={(_evt, { checked }: { checked: boolean }) =>
                  toggleAll(checked)
                }
              />
              <div className={styles.cards}>
                {charts.map((chart) => (
                  <div key={chart.instanceId} className={styles.chartRow}>
                    <Checkbox
                      id={`eval3-chart-${chart.instanceId}`}
                      labelText={chart.name}
                      checked={chart.visible}
                      onChange={(_evt, { checked }: { checked: boolean }) =>
                        dispatchCharts({
                          type: 'setVisible',
                          instanceId: chart.instanceId,
                          visible: checked,
                        })
                      }
                    />
                    <div className={styles.rowActions}>
                      <Button
                        kind="ghost"
                        hasIconOnly
                        size="sm"
                        renderIcon={Edit}
                        iconDescription={`Edit ${chart.name}`}
                        tooltipPosition="bottom"
                        onClick={() => setEditChart(chart)}
                      />
                      <Button
                        kind="ghost"
                        hasIconOnly
                        size="sm"
                        renderIcon={TrashCan}
                        iconDescription={`Delete ${chart.name}`}
                        tooltipPosition="bottom"
                        onClick={() =>
                          dispatchCharts({
                            type: 'remove',
                            instanceId: chart.instanceId,
                          })
                        }
                      />
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {addChartOpen && (
          <AddChartModal
            catalog={catalog}
            isLoading={catalogLoading}
            error={catalogError ? catalogError.message : null}
            onAdd={handleApplyCharts}
            onRetry={() => refetchCatalog()}
            onClose={() => setAddChartOpen(false)}
          />
        )}
        {editChart && (
          <ChartModal
            initialName={editChart.name}
            title="Edit chart"
            submitLabel="Save"
            onSubmit={handleRenameSubmit}
            onClose={() => setEditChart(null)}
          />
        )}
      </AccordionDetails>
    </Accordion>
  );
}
